import React from 'react';
import {
    FireIcon,
    SparklesIcon,
    SunIcon,
    BoltIcon,
    CloudIcon
} from '@heroicons/react/24/solid';

// This HeatLevel type is self-contained and matches Proposal['heatLevel']
export type HeatLevel = 'frozen' | 'cold' | 'cool' | 'warm' | 'hot' | 'burning';

interface ThermometerProps {
    heatLevel: HeatLevel;       // Renamed from level
    onHeatLevelChange?: (level: HeatLevel) => void; // Renamed from onChange
    className?: string;
    mode?: 'form' | 'display';
    customText?: string;
    disabled?: boolean; // Added disabled prop
}

const heatLevelsConfig: Record<HeatLevel, {
    color: string;
    bgColor: string;
    label: string;
    icon?: React.ReactNode;
    barColor: string;
}> = {
    frozen: {
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        label: 'Congelado',
        icon: <SparklesIcon className="w-5 h-5" />,
        barColor: 'bg-blue-600'
    },
    cold: {
        color: 'text-blue-500',
        bgColor: 'bg-blue-50',
        label: 'Frio',
        icon: <CloudIcon className="w-5 h-5" />,
        barColor: 'bg-blue-500'
    },
    cool: {
        color: 'text-green-500',
        bgColor: 'bg-green-50',
        label: 'Fresco',
        icon: <BoltIcon className="w-5 h-5" />,
        barColor: 'bg-green-500'
    },
    warm: {
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-50',
        label: 'Morno',
        icon: <SunIcon className="w-5 h-5" />,
        barColor: 'bg-yellow-500'
    },
    hot: {
        color: 'text-orange-500',
        bgColor: 'bg-orange-50',
        label: 'Quente',
        icon: <FireIcon className="w-5 h-5" />,
        barColor: 'bg-orange-500'
    },
    burning: {
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        label: 'Em Chamas',
        icon: <FireIcon className="w-5 h-5 text-red-600" />,
        barColor: 'bg-red-600'
    }
};

export default function Thermometer({
    heatLevel,                // Renamed from level
    onHeatLevelChange,        // Renamed from onChange
    className = '',
    mode = 'form',
    customText,
    disabled = false        // Added disabled prop
}: ThermometerProps) {
    const currentLevelConfig = heatLevelsConfig[heatLevel];
    const levels = Object.keys(heatLevelsConfig) as HeatLevel[];
    const currentIndex = levels.indexOf(heatLevel);

    if (mode === 'form') {
        return (
            <div className={`flex items-center space-x-2 ${className}`}>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                        className={`h-full transition-all duration-300 ${currentLevelConfig.barColor}`}
                        style={{ width: `${((currentIndex + 1) / levels.length) * 100}%` }}
                    />
                </div>
                {onHeatLevelChange ? (
                    <select
                        value={heatLevel}
                        onChange={(e) => onHeatLevelChange(e.target.value as HeatLevel)}
                        className="text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                        disabled={disabled} // Applied disabled prop
                    >
                        {levels.map((l) => (
                            <option key={l} value={l}>
                                {heatLevelsConfig[l].label}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span className="text-sm font-medium text-gray-700">{currentLevelConfig.label}</span>
                )}
            </div>
        );
    }

    return (
        <div className={`flex flex-col items-end ${className}`}>
            <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-full ${currentLevelConfig.bgColor} ${currentLevelConfig.color}`}>
                {currentLevelConfig.icon}
                <span className="font-medium text-sm">{customText || currentLevelConfig.label}</span>
            </div>
        </div>
    );
} 